import React, { type JSX } from 'react';
import { type CategoryInfo } from '../../models/CategoryInfo';
import { NavLink } from 'react-router-dom';
import { ImgEdit } from '../Images/ImgEdit';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Skeleton } from '../Skeleton';

import './_rail.scss';

const untitledNoteTitle = 'Untitled note';

export interface CategoryRailProps {
  categories: CategoryInfo[];
  activeCategoryId: string;
  noteCounts: Record<string, number>;
  totalNoteCount: number;
  isArchive: boolean;
  onAllClick: () => void;
  onCategoryClick: (cat: CategoryInfo) => void;
  onAddCategory: () => void;
  onCollapse: () => void;
  onCategoryDragEnd?: (event: DragEndEvent) => void;
  onCategoryEdit?: (cat: CategoryInfo) => void;
  // True while the initial load is pending — shows a skeleton list.
  isLoading?: boolean;
  // Search mode — a "Search" row appears below Dashboard and stays active.
  isSearchMode: boolean;
  searchCount: number;
  searchQuery: string;
  onDashboardClick: () => void;
  onSearchClick: () => void;
}

const DEFAULT_CATEGORY_ID = 'default';

function Dot({ color, all }: { color?: string, all?: boolean }): JSX.Element {
  const style: React.CSSProperties | undefined = color && !all ? { background: color } : undefined;
  return <span className={`cat-dot${all ? ' all' : ''}`} style={style} />;
}

interface SortableCatProps {
  cat: CategoryInfo;
  active: boolean;
  count: number;
  isTouchDevice: boolean;
  onEdit?: (cat: CategoryInfo) => void;
  onClick: () => void;
}

function EditButton({ cat, onEdit }: { cat: CategoryInfo; onEdit: (cat: CategoryInfo) => void }): JSX.Element {
  return (
    <span
      className="cat-edit"
      title="Edit category"
      aria-label="Edit category"
      onClick={(e) => { e.stopPropagation(); onEdit(cat); }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <ImgEdit size={13} />
    </span>
  );
}

function SortableCat({ cat, active, count, isTouchDevice, onEdit, onClick }: SortableCatProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cat.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cat-item cat-item-draggable${active ? ' active' : ''}`}
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      <Dot color={cat.color} />
      <span className="cat-name">{cat.name || untitledNoteTitle}</span>
      {onEdit && <EditButton cat={cat} onEdit={() => onEdit(cat)} />}
      <span className="cat-count">{count ?? 0}</span>
      {isTouchDevice && (
        <span className="cat-drag-handle">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" /><circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" /><circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" /></svg>
        </span>
      )}
    </div>
  );
}

function StaticCat({ cat, active, count, onClick, onEdit }: { cat: CategoryInfo; active: boolean; count: number; onClick: () => void; onEdit?: (cat: CategoryInfo) => void }): JSX.Element {
  return (
    <div className={`cat-item${active ? ' active' : ''}`} onClick={onClick}>
      <Dot color={cat.color} />
      <span className="cat-name">{cat.name || untitledNoteTitle}</span>
      {onEdit && <EditButton cat={cat} onEdit={() => onEdit(cat)} />}
      <span className="cat-count">{count ?? 0}</span>
    </div>
  );
}

export default function CategoryRail(props: CategoryRailProps): JSX.Element {
  const {
    categories, activeCategoryId, noteCounts, totalNoteCount, isArchive,
    onAllClick, onCategoryClick, onAddCategory, onCollapse, onCategoryDragEnd, onCategoryEdit,
    isLoading, isSearchMode, searchCount, searchQuery, onDashboardClick, onSearchClick
  } = props;

  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8, delay: isTouchDevice ? 400 : 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // The virtual Default category is a fixed, non-draggable row right after "All notes".
  const defaultCat = categories.find(c => c.id === DEFAULT_CATEGORY_ID);
  const realCategories = categories.filter(c => c.id !== DEFAULT_CATEGORY_ID);

  return (
    <aside className="rail">
      <div className="rail-head">
        <span className="rail-title">Categories</span>
        <div className="rail-head-actions">
          <button className="icon-btn sm" title="Add category" aria-label="Add category" onClick={onAddCategory}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          <button className="icon-btn sm" title="Collapse" aria-label="Collapse categories" onClick={onCollapse}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="9" y1="4" x2="9" y2="20" /></svg>
          </button>
        </div>
      </div>

      {isLoading ? (
        <nav className="cat-list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cat-skeleton-item">
              <span className="cat-skeleton-dot" />
              <div className="cat-skeleton-name">
                <Skeleton height={12} width={`${i % 2 === 0 ? 72 : 56}%`} />
              </div>
            </div>
          ))}
        </nav>
      ) : (
      <nav className="cat-list">
        <NavLink
          to="/"
          className={`cat-item nav${window.location.pathname === '/' && !isSearchMode ? ' active' : ''}`}
          onClick={onDashboardClick}
        >
          <svg className="cat-nav-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          <span className="cat-name">Dashboard</span>
        </NavLink>

        {isSearchMode && (
          <div className="cat-item search active" title={searchQuery ? `“${searchQuery}”` : 'Search'} onClick={onSearchClick}>
            <svg className="cat-search-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <span className="cat-name">Search</span>
            <span className="cat-count">{searchCount}</span>
          </div>
        )}

        <div className={`cat-item all${activeCategoryId === '' && !isArchive && !isSearchMode ? ' active' : ''}`} onClick={onAllClick}>
          <Dot all />
          <span className="cat-name">All notes</span>
          <span className="cat-count">{totalNoteCount}</span>
        </div>

        {defaultCat && (
          <StaticCat
            cat={defaultCat}
            active={activeCategoryId === DEFAULT_CATEGORY_ID && !isSearchMode}
            // Uncategorized notes carry an empty categoryId — the virtual row's key is ''.
            count={noteCounts[''] ?? 0}
            onClick={() => onCategoryClick(defaultCat)}
          />
        )}

        {onCategoryDragEnd ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCategoryDragEnd}>
            <SortableContext items={realCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
              {realCategories.map(c => (
                <SortableCat
                  key={c.id}
                  cat={c}
                  active={c.id === activeCategoryId && !isSearchMode}
                  count={noteCounts[c.id] ?? 0}
                  isTouchDevice={isTouchDevice}
                  onEdit={onCategoryEdit}
                  onClick={() => onCategoryClick(c)}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          realCategories.map(c => (
            <StaticCat
              key={c.id}
              cat={c}
              active={c.id === activeCategoryId && !isSearchMode}
              count={noteCounts[c.id] ?? 0}
              onEdit={onCategoryEdit}
              onClick={() => onCategoryClick(c)}
            />
          ))
        )}
      </nav>
      )}
    </aside>
  );
}
